import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import JsonLd from "@/components/feature/JsonLd";
import { buildArticleSchema, buildFaqPageSchema } from "@/constants/schema";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/constants/site";
import { articles, DEFAULT_ARTICLE_SLUG, articleSlugs } from "@/constants/articles";

interface Section {
  title: string;
  body: string[];
}

interface FaqItem {
  q: string;
  a: string;
}

interface ArticleContent {
  title: string;
  description: string;
  intro: string;
  sections: Section[];
  faqs: FaqItem[];
  conclusion: string;
}

export default function Article() {
  const { lang, slug } = useParams();
  const currentLang = useLanguage(lang);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const resolvedSlug = slug && articleSlugs.includes(slug) ? slug : DEFAULT_ARTICLE_SLUG;
  const meta = articles.find((a) => a.slug === resolvedSlug) || articles[0];

  const content = t(`articles.${resolvedSlug}`, { returnObjects: true }) as ArticleContent;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [resolvedSlug, lang]);

  useEffect(() => {
    if (slug && slug !== resolvedSlug) {
      navigate(`/${lang || "en"}/article/${resolvedSlug}`, { replace: true });
    }
  }, [slug, resolvedSlug, lang, navigate]);

  const otherArticles = articles.filter((a) => a.slug !== resolvedSlug).slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={buildArticleSchema({
          lang: currentLang,
          slug: resolvedSlug,
          title: content?.title || "",
          description: content?.description || "",
          image: meta.image,
        })}
      />
      <JsonLd data={buildFaqPageSchema(content?.faqs || [])} />
      <Navbar />
      <main className="pt-24 md:pt-28">
        <article className="mx-auto w-full max-w-3xl px-4 pb-16 md:px-6">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
            <Link to={`/${lang}`} className="transition-colors hover:text-primary-700">
              {t("brand.name")}
            </Link>
            <span className="mx-2">›</span>
            <Link
              to={`/${lang}#guides`}
              className="transition-colors hover:text-primary-700"
            >
              {t("nav.guides")}
            </Link>
          </nav>

          {/* Header */}
          <div className="mt-4 flex items-start gap-4">
            <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
              <i className={`${meta.icon} text-2xl leading-none`} />
            </span>
            <h1 className="text-2xl font-extrabold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              {content?.title}
            </h1>
          </div>
          <p className="mt-3 text-sm text-foreground-500">{t("article.updated")}</p>

          {/* Quick answer */}
          <div className="mt-6 rounded-xl border border-primary-200 bg-primary-50 p-5 md:p-6">
            <div className="flex items-center gap-2">
              <i className="ri-flashlight-line flex h-4 w-4 items-center justify-center text-primary-700" />
              <span className="text-sm font-semibold text-primary-800">
                {t("article.quickAnswerLabel")}
              </span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
              {content?.intro}
            </p>
          </div>

          {/* Table of contents */}
          <div className="mt-8 rounded-xl border border-background-200 bg-background-50 p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground-500">
              {t("article.tocTitle")}
            </h2>
            <ul className="mt-3 space-y-2">
              {(content?.sections || []).map((section, i) => (
                <li key={section.title}>
                  <a
                    href={`#section-${i}`}
                    className="text-sm text-foreground-700 transition-colors hover:text-primary-700"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          <div className="mt-10 space-y-10">
            {(content?.sections || []).map((section, i) => (
              <section key={section.title} id={`section-${i}`}>
                <h2 className="scroll-mt-24 text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
                  {section.title}
                </h2>
                {section.body.map((paragraph, j) => (
                  <p key={j} className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}
          </div>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              {t("article.faqTitle")}
            </h2>
            <div className="mt-5 space-y-4">
              {(content?.faqs || []).map((faq) => (
                <div key={faq.q} className="rounded-xl border border-background-200 bg-background-50 p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 md:text-base">{faq.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section className="mt-12 rounded-xl bg-background-100 p-6 md:p-8">
            <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
              {t("article.conclusionTitle")}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {content?.conclusion}
            </p>
          </section>

          {/* Related articles */}
          {otherArticles.length > 0 && (
            <section className="mt-12">
              <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">
                {t("article.relatedTitle")}
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {otherArticles.map((a) => {
                  const related = t(`articles.${a.slug}`, { returnObjects: true }) as ArticleContent;
                  return (
                    <Link
                      key={a.slug}
                      to={`/${lang}/article/${a.slug}`}
                      className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={`${a.icon} text-base leading-none`} />
                      </span>
                      <span className="text-sm font-medium text-foreground-800">{related?.title}</span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Internal links / CTA */}
          <div className="mt-10 flex flex-col gap-4 border-t border-background-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to={`/${lang}`}
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
            >
              <i className="ri-arrow-left-line flex h-4 w-4 items-center justify-center text-base leading-none" />
              {t("article.backHome")}
            </Link>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t("article.ctaLabel")}
              <i className="ri-arrow-right-line flex h-4 w-4 items-center justify-center text-base leading-none" />
            </a>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}